import { inject, Inject, Injectable, Optional } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { map } from 'rxjs/operators';
import { Location } from '@angular/common';
import { NavController, Platform } from '@ionic/angular';
import { SeoSetupService } from '@b2b/seo-util-lib';
import { AnalyticsService } from '@app/api-v2/util/analytics/analytics.service';
import { environment } from '@src/environments/environment';

@Injectable({ providedIn: 'root' })
export class B2bRouterService {
  private _history: string[] = [];
  private _rootUrl!: string;
  private _fallbackPrefix!: string;
  private analyticsService: AnalyticsService;
  private isMobileApp: boolean;

  constructor(
    private _router: Router,
    private _location: Location,
    private navCtrl: NavController,
    private platform: Platform,
    @Optional() @Inject('WINDOW') private window: any,
    private seoSetupService: SeoSetupService,
  ) {
    this.analyticsService = inject(AnalyticsService);
    this._rootUrl = '/';
    this._fallbackPrefix = '';

    // Determine if we're in mobile app or web
    this.isMobileApp = environment?.mobileApp;

    this._router.events
      .pipe(
        filter((event) => {
          if (event instanceof NavigationStart) {
            if (event?.url?.indexOf('page-not-found') > -1) {
              this.popHistory();
            }
            this.seoSetupService?.resetSeoData();
          }
          if (event instanceof NavigationEnd) {
          }
          return event instanceof NavigationEnd;
        }),
        map((event: NavigationEnd) => {
          this.analyticsService?.setScreenName(event?.urlAfterRedirects);
          return event;
        }),
      )
      .subscribe((event) => {
        this.setHistory(event?.urlAfterRedirects);
      });

    if (this._router?.url) {
      this.setHistory(this._router?.url);
    }
  }

  popHistory() {
    if (this._history?.length > 0) {
      return this._history.pop();
    }
    return undefined;
  }

  setHistory(url: string) {
    if (!this._history) {
      this._history = [];
    }
    this._history = this._history?.filter((item: string) => {
      return item !== url;
    });
    if (
      url?.indexOf('page-not-found') > -1 ||
      url?.indexOf('feature/checkout/verify-transaction') > -1
    ) {
    } else {
      this._history?.push(url);
    }
  }

  back(fallback?: string): void {
    fallback = this._fallbackPrefix + (fallback || this._rootUrl);

    if (this.isMobileApp) {
      // Use Ionic navigation for mobile apps
      this.handleMobileBack(fallback);
    } else {
      // Use web navigation for web apps
      this.handleWebBack(fallback);
    }
  }

  private handleMobileBack(fallback: string): void {
    const record = this.popHistory();

    // For mobile apps, try NavController.back() first
    // If it fails or there's no navigation history, use fallback
    if (this._history?.length > 0) {
      try {
        this.navCtrl?.back();
      } catch (error) {
        console.error('Ionic back navigation failed:', error);
        // Fallback to router navigation
        this._router?.navigateByUrl(fallback)?.then();
      }
    } else {
      // No history, navigate to fallback
      this._router?.navigateByUrl(fallback)?.then();
    }
  }

  private handleWebBack(fallback: string): void {
    const record = this.popHistory();

    if (this._history?.length > 0) {
      if (this._history?.length === 1) {
        const path = this._history[0];
        if (path === '/') {
          this._router?.navigateByUrl('/')?.then();
          return;
        }
      }

      this._location?.back();
    } else {
      if (this.window) {
        try {
          this.window.history.replaceState(null, '', fallback);
        } catch (e) {}
        try {
          this.window.history.pushState(null, '', record ?? this._router.url);
        } catch (e) {
          console.error('History manipulation failed:', e);
        }
        this._location.back();
      } else {
        this._router?.navigateByUrl('/').then();
      }
    }
  }

  // Check if we can go back based on our history tracking
  canGoBack(): boolean {
    return this._history?.length > 0;
  }

  // Method to get current navigation context
  getNavigationContext(): 'mobile' | 'web' {
    return this.isMobileApp ? 'mobile' : 'web';
  }

  // Method to force navigation to root
  navigateToRoot(): void {
    if (this.isMobileApp) {
      this.navCtrl?.navigateRoot(this._rootUrl);
    } else {
      this._router?.navigateByUrl(this._rootUrl).then();
    }
  }

  // Get current history length
  getHistoryLength(): number {
    return this._history?.length;
  }

  // Clear navigation history
  clearHistory(): void {
    this._history = [];
  }

  // Get current history stack (for debugging)
  getHistory(): string[] {
    return [...this._history]; // Return a copy
  }

  // Method to check if current URL is root
  isAtRoot(): boolean {
    const currentUrl = this._router?.url;
    return currentUrl === this._rootUrl || currentUrl === '/';
  }
}
